import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { AlertTriangle, Gem, Loader, Rss } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { LiveFeedActivity } from "@shared/schema"; // Import the new type

const fetchRecentActivity = async (): Promise<LiveFeedActivity[]> => {
    const response = await fetch("/api/public/transactions");
    if (!response.ok) {
      throw new Error("Failed to fetch recent activity");
    }
    return response.json();
};

const getActionText = (provider: string): string => {
    switch (provider) {
        case 'lootably':
            return 'Completed an offer';
        case 'sponsored_ad':
            return 'Played a sponsored game';
        case 'clipping':
            return 'Earned from a clip';
        default:
            return `Earned from ${provider}`;
    }
};

export default function LiveFeed() {
    const { data: recentActivity, isLoading, isError } = useQuery<LiveFeedActivity[]>({ 
        queryKey: ["recentActivityHomepage"],
        queryFn: fetchRecentActivity,
        refetchInterval: 15000,
    });

    // Create a duplicated list for the seamless animation
    const activityFeed = recentActivity ? [...recentActivity, ...recentActivity] : [];

    return (
        <section className="py-20 overflow-hidden">
            <div className="container mx-auto max-w-6xl">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.5 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12"
                >
                    <h2 className="text-3xl font-bold tracking-tight flex items-center justify-center gap-2">
                        <Rss className="w-7 h-7 text-primary"/>
                        Live Earning Feed
                    </h2>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto mt-2">
                        See what other users are earning in real-time.
                    </p>
                </motion.div>
            </div>
            
            <div className="relative h-24 flex items-center">
                 {/* Left fade */}
                <div className="absolute top-0 bottom-0 left-0 w-24 z-10 bg-gradient-to-r from-background to-transparent" />
                
                {activityFeed.length > 0 && (
                    <motion.div 
                        className="flex gap-4"
                        initial={{ x: 0 }}
                        animate={{ x: '-50%' }}
                        transition={{
                            ease: 'linear',
                            duration: activityFeed.length * 2, // Adjust duration based on items
                            repeat: Infinity,
                        }}
                    >
                        {activityFeed.map((activity, index) => (
                           <Card 
                                key={`${activity.id}-${index}`} 
                                className="w-64 flex-shrink-0 bg-background/50 border-border/50 hover:border-primary/50 transition-colors duration-300 group"
                            >
                                <CardContent className="p-3 flex items-center gap-3">
                                    <Avatar className="h-12 w-12 border-2 border-border/50 group-hover:border-primary/80 transition-colors">
                                        <AvatarImage src={activity.user.avatarUrl} alt={activity.user.displayName} />
                                        <AvatarFallback>{activity.user.displayName.charAt(0).toUpperCase()}</AvatarFallback>
                                    </Avatar>
                                    <div className="overflow-hidden">
                                        <p className="font-semibold text-sm truncate text-white">{activity.user.displayName}</p>
                                        <p className="text-xs text-muted-foreground truncate">{getActionText(activity.provider)}</p>
                                        <div className="flex items-center gap-1.5 text-primary font-bold text-sm mt-1">
                                            <Gem className="w-3.5 h-3.5" />
                                            <span>+{activity.tokensEarned.toLocaleString()}</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </motion.div>
                )}
                
                {/* Right fade */}
                <div className="absolute top-0 bottom-0 right-0 w-24 z-10 bg-gradient-to-l from-background to-transparent" />
            </div>

            {isLoading && !recentActivity && (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader className="w-5 h-5 mr-2 animate-spin" />
                    <span>Loading live earnings...</span>
                </div>
            )}
            {isError && (
                <div className="flex items-center justify-center py-8 text-destructive">
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    <span>Failed to load activity.</span>
                </div>
            )}
        </section>
    )
}